'use client';

import { ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { EServiceShowcaseColumns, IServiceShowcaseItem } from '@/components/service-showcase/type';
import { twMerge } from 'tailwind-merge';
import ServiceShowcaseIcon from '@/components/service-showcase/ServiceShowcaseIcon';

export interface IServiceShowcaseClientProps {
  editable?: Record<string, string | undefined>;
  items: IServiceShowcaseItem[];
  panelContents: ReactNode[];
  columns?: EServiceShowcaseColumns;
  gridLabel?: string;
}

const MD_LAST_ROW_SPAN: Record<number, string> = {
  1: 'md:col-span-6',
  2: 'md:col-span-3',
  3: 'md:col-span-2',
};

function getColSpan(visibleIdx: number, total: number): string {
  const lastRowCount = total % 3 === 0 ? 3 : total % 3;
  const firstRowsCount = total - lastRowCount;
  const mdSpan = visibleIdx < firstRowsCount ? 'md:col-span-2' : (MD_LAST_ROW_SPAN[lastRowCount] ?? 'md:col-span-2');
  const mobileSpan = 'col-span-1';
  return `${mobileSpan} ${mdSpan} xl:col-span-1`;
}

function getGridClassName(columns: EServiceShowcaseColumns = '5-col'): string {
  switch (columns) {
    case '3-col':
      return 'grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3';
    case '2-col':
      return 'grid grid-cols-1 gap-4 xl:grid-cols-2';
    default:
      return 'grid grid-cols-1 gap-4 md:grid-cols-6 xl:grid-cols-5';
  }
}

export default function ServiceShowcaseClient({
  editable,
  items,
  panelContents,
  columns = '5-col',
  gridLabel,
}: IServiceShowcaseClientProps) {
  const [displayIndex, setDisplayIndex] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const [leavingIndex, setLeavingIndex] = useState<number | null>(null);

  const rootRef = useRef<HTMLDivElement>(null);
  const panelOuterRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const wrapperRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const prevPositions = useRef<Map<number, DOMRect>>(new Map());
  const enteringIdxRef = useRef<number | null>(null);
  const shouldFlip = useRef(false);

  const active = items[displayIndex];

  const visibleItems = items.map((item, idx) => ({ item, idx })).filter(({ idx }) => idx !== activeIndex);

  useLayoutEffect(() => {
    if (!shouldFlip.current) return;
    shouldFlip.current = false;

    const enteringIdx = enteringIdxRef.current;

    wrapperRefs.current.forEach((el, idx) => {
      if (idx === enteringIdx) {
        el.style.transition = 'none';
        el.style.transform = 'scale(0.85)';
        el.style.opacity = '0';
      } else {
        const prev = prevPositions.current.get(idx);
        if (!prev) return;
        const curr = el.getBoundingClientRect();
        const dx = prev.left - curr.left;
        const dy = prev.top - curr.top;
        if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) return;
        el.style.transition = 'none';
        el.style.transform = `translate(${dx}px, ${dy}px)`;
      }
    });

    requestAnimationFrame(() => {
      wrapperRefs.current.forEach((el, idx) => {
        if (idx === enteringIdx) {
          el.style.transition = 'transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.28s ease-out';
          el.style.transform = 'scale(1)';
          el.style.opacity = '1';
        } else {
          el.style.transition = 'transform 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
          el.style.transform = '';
        }
      });
    });
  }, [activeIndex]);

  function handleCardClick(originalIdx: number) {
    if (leavingIndex !== null) return;
    setLeavingIndex(originalIdx);

    if (window.innerWidth < 768) {
      rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    prevPositions.current.clear();
    wrapperRefs.current.forEach((el, idx) => {
      prevPositions.current.set(idx, el.getBoundingClientRect());
    });
    enteringIdxRef.current = activeIndex;
    shouldFlip.current = true;

    const panelOuterEl = panelOuterRef.current;
    const panelEl = panelRef.current;
    const cardEl = wrapperRefs.current.get(originalIdx);

    if (panelOuterEl) panelOuterEl.style.minHeight = `${panelOuterEl.offsetHeight}px`;

    if (panelEl) {
      panelEl.style.transition = 'opacity 0.12s ease-in, transform 0.12s ease-in';
      panelEl.style.opacity = '0';
      panelEl.style.transform = 'translateY(-6px)';
    }

    if (cardEl) {
      cardEl.style.transition = 'opacity 0.12s ease-in, transform 0.12s ease-in';
      cardEl.style.opacity = '0';
      cardEl.style.transform = 'scale(0.88)';
    }

    setTimeout(() => {
      if (panelEl) {
        panelEl.style.transition = 'none';
        panelEl.style.opacity = '0';
        panelEl.style.transform = 'translateY(6px)';
      }

      setDisplayIndex(originalIdx);
      setActiveIndex(originalIdx);
      setLeavingIndex(null);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (panelEl) {
            panelEl.style.transition = 'opacity 0.22s ease-out, transform 0.22s ease-out';
            panelEl.style.opacity = '1';
            panelEl.style.transform = 'translateY(0)';
          }
        });
      });

      setTimeout(() => {
        if (panelOuterEl) panelOuterEl.style.minHeight = '';
      }, 260);
    }, 130);
  }

  return (
    <div ref={rootRef} {...editable} className='flex flex-col gap-6'>
      {/* Active Item Panel */}
      <div
        ref={panelOuterRef}
        data-testid='service-showcase-panel'
        className='overflow-hidden rounded-3.5xl bg-service-showcase-panel border border-white/10'
      >
        <div className='flex flex-col lg:flex-row'>
          {/* Left content — padding lives here so the visual section can go edge-to-edge */}
          <div ref={panelRef} className='flex-1 p-6 md:p-8'>
            {/* Eyebrow + title pill */}
            <div className='mb-4 flex items-center gap-3'>
              {active.eyebrow && <span className='text-primary-aqua text-sm'>{active.eyebrow}</span>}
              <h2 className='border-primary-aqua/20 bg-primary-aqua/10 text-primary-aqua/60 m-0 rounded-full border px-4 py-1.5 text-sm font-normal tracking-widest uppercase'>
                {active.title}
              </h2>
            </div>
            {/* Description */}
            {active.description && <p className='text-primary-aqua mb-6 text-lg italic'>{active.description}</p>}
            {/* Panel content (childNodes) */}
            {panelContents[displayIndex] && <div>{panelContents[displayIndex]}</div>}
          </div>

          {/* Right visual section — mirrors .visual from the HTML, grid fills edge-to-edge */}
          {active.icon && (
            <div className='relative flex items-center justify-center lg:self-stretch lg:border-l lg:border-white/10'>
              {/* Dot-grid overlay identical to .visual::before in the HTML */}
              <div className='service-showcase-visual-grid' aria-hidden='true' />
              {/* Icon + optional taglines */}
              <div key={displayIndex} className='animate-fade-in-right relative flex w-96 flex-col p-6 lg:scale-150 lg:p-8'>
                {active.iconLabel && (
                  <p className='text-primary-white/40 mb-1 self-end font-mono text-xs tracking-widest uppercase'>
                    {active.iconLabel}
                  </p>
                )}
                <ServiceShowcaseIcon icon={active.icon} className='text-primary-aqua w-full' />
                {active.iconCaption && (
                  <p className='text-primary-aqua/60 mt-2 font-mono text-xs tracking-widest uppercase'>
                    {active.iconCaption}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Grid label separator */}
      {gridLabel && (
        <div className='text-primary-white/60 flex items-center gap-4 uppercase'>
          <span className='bg-primary-white/40 block h-px w-8 shrink-0' />
          <span>{gridLabel}</span>
        </div>
      )}

      {/* Service Grid */}
      <div data-testid='service-showcase-cards' className={getGridClassName(columns)}>
        {visibleItems.map(({ item, idx }, visibleIdx) => (
          <div
            key={item._uid ?? idx}
            ref={(el) => {
              if (el) wrapperRefs.current.set(idx, el);
              else wrapperRefs.current.delete(idx);
            }}
            className={columns === '5-col' ? getColSpan(visibleIdx, visibleItems.length) : 'col-span-1'}
          >
            <div
              role='button'
              tabIndex={0}
              onClick={() => handleCardClick(idx)}
              onKeyDown={(e) => e.key === 'Enter' && handleCardClick(idx)}
              className={twMerge(
                'rounded-tl-3.5xl rounded-b-3.5xl border-primary-aqua/30 bg-primary-blueLightNight flex h-full w-full flex-col border',
                'cursor-pointer px-6 py-7 text-left transition-colors duration-300',
                'hover:border-primary-aqua'
              )}
            >
              {item.eyebrow && (
                <div className='text-primary-aqua mb-4 flex items-center gap-x-3'>
                  <span className='bg-primary-aqua block h-px w-3.5' />
                  <span className='text-base font-medium tracking-widest'>{item.eyebrow}</span>
                </div>
              )}
              <div className='flex-1'>
                <h3 className='text-md text-primary-black dark:text-primary-white font-semibold tracking-normal'>
                  {item.title}
                </h3>
                {item.description && (
                  <p className='text-primary-aqua mt-3 text-sm whitespace-break-spaces italic'>{item.description}</p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
